import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import SelectApplication from "components/SelectApplication";
import SelectUserCredentialType from "components/SelectUserCredentialType";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  SaveUserCredentialsRequest,
  UserCredentialModel,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";

type UserCredentialFormValues = Pick<
  SaveUserCredentialsRequest,
  | "id"
  | "externalUserId"
  | "organizationUserId"
  | "applicationId"
  | "userCredentialTypeId"
>;

const { Form } = withTypes<UserCredentialFormValues>();

type EditUserCredentialDialogProps = {
  organizationUserId: string;
  userCredential?: UserCredentialModel;
  open: boolean;
  close: () => void;
};

export default function EditUserCredentialDialog({
  organizationUserId,
  userCredential,
  open,
  close,
}: EditUserCredentialDialogProps) {
  const { formatMessage: t } = useIntl();
  const { userCredentialService } = useGlobalContext();
  const queryClient = useQueryClient();

  const init = useMemo<UserCredentialFormValues>(
    () => ({
      id: userCredential?.id,
      applicationId: userCredential?.applicationId,
      externalUserId: userCredential?.externalUserId!,
      organizationUserId: organizationUserId,
      userCredentialTypeId: userCredential?.userCredentialTypeId,
    }),
    [organizationUserId, userCredential]
  );

  const saveUserCredential = async (values: UserCredentialFormValues) => {
    try {
      await userCredentialService.saveUserCredentials(
        new SaveUserCredentialsRequest({
          ...values,
          externalUserId: values.externalUserId?.trim(),
        })
      );

      queryClient.invalidateQueries("getUserCredentials");
      close();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <Form
        onSubmit={saveUserCredential}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="userCredentialTypeId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <SelectUserCredentialType
                          fullWidth
                          variant="standard"
                          label={t({ id: "typeLabel" })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                            helperText:
                              meta.error && meta.submitFailed && meta.error,
                          }}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="externalUserId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label={t({ id: "externalUserId" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="applicationId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <SelectApplication
                          fullWidth
                          variant="standard"
                          label={t({ id: "applicationLabel" })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                            helperText:
                              meta.error && meta.submitFailed && meta.error,
                          }}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={close}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={16} />}
                >
                  {t({ id: "saveButton" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
}
